import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Setup } from "../api";
import Chart, { type ChartOverlay } from "../components/Chart";

type LabelValue = "good" | "bad" | "borderline";

export default function LabelView() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(0);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  // Match the Streamlit Label view: same default filter as the List view
  // (origin=manual). The user can pivot off /label later if needed; for
  // now this is the queue we want to grade against.
  const setupsQ = useQuery({
    queryKey: ["setups", { origin: "manual", limit: 200 }],
    queryFn: () => api.setups({ origin: "manual", limit: 200 }),
  });
  const labelsQ = useQuery({
    queryKey: ["labels"],
    queryFn: () => api.labels(),
  });

  const queue: Setup[] = useMemo(() => {
    if (!setupsQ.data || !labelsQ.data) return [];
    const labelled = new Set(
      labelsQ.data.labels
        .map((l) => l.setup_id)
        .filter((v): v is string => typeof v === "string"),
    );
    return setupsQ.data.setups.filter(
      (s) => s.setup_id && !labelled.has(s.setup_id as string),
    );
  }, [setupsQ.data, labelsQ.data]);

  const labelledCount = labelsQ.data?.labels.length ?? 0;

  const current = queue[Math.min(cursor, Math.max(0, queue.length - 1))];

  const barsQ = useQuery({
    queryKey: ["bars", current?.symbol, current?.timeframe],
    queryFn: () => api.bars(current!.symbol, current!.timeframe),
    enabled: !!current,
  });

  const overlay = useMemo<ChartOverlay | undefined>(() => {
    if (!current) return undefined;
    return {
      spike: [Number(current.leg_start_idx), Number(current.leg_end_idx)],
      range: [Number(current.tr_start_idx), Number(current.tr_end_idx)],
      tr_high: Number(current.tr_high),
      tr_low: Number(current.tr_low),
      target: Number(current.measured_move_target),
      breakout:
        current.breakout_idx !== null && current.breakout_idx !== undefined
          ? {
              idx: Number(current.breakout_idx),
              with_trend: current.breakout_direction === "with_trend",
            }
          : undefined,
    };
  }, [current]);

  const labelMut = useMutation({
    mutationFn: (label: LabelValue) =>
      api.createLabel({
        symbol: current!.symbol,
        tf: current!.timeframe,
        setup_id: current!.setup_id as string,
        label,
        notes,
      }),
    onSuccess: () => {
      // Invalidate labels so this row drops out of the queue. The cursor
      // can stay where it is — the next setup slides into this slot.
      qc.invalidateQueries({ queryKey: ["labels"] });
      setNotes("");
      setShowNotes(false);
    },
  });

  if (setupsQ.isLoading || labelsQ.isLoading) {
    return <p className="muted">Loading queue…</p>;
  }
  if (setupsQ.error || labelsQ.error) {
    return <p className="muted">Failed to load queue.</p>;
  }
  if (!setupsQ.data || setupsQ.data.setups.length === 0) {
    return (
      <p className="muted">
        No setups in the queue. Run <code>./edge nightly</code> or use
        Create to draw one by hand.
      </p>
    );
  }
  if (queue.length === 0) {
    return (
      <div>
        <h2 style={{ margin: "4px 0 12px" }}>All caught up</h2>
        <p className="muted">
          Every setup in the current queue has been labelled.
          <br />
          {labelledCount} labels saved.
        </p>
      </div>
    );
  }
  if (!current) return null;

  const dirColor = current.direction === "bull" ? "var(--good)" : "var(--bad)";
  const total = queue.length;
  const errMsg =
    labelMut.error instanceof ApiError ? labelMut.error.message : null;

  return (
    <div>
      <div className="detail-header">
        <h2>
          {current.symbol} <span className="muted">· {current.timeframe}</span>
        </h2>
        <span
          className="dir-pill"
          style={{ color: dirColor, borderColor: dirColor }}
        >
          {String(current.direction).toUpperCase()}
        </span>
        <span style={{ flex: 1 }} />
        <span className="muted small">
          {Math.min(cursor + 1, total)} / {total}
          {labelledCount > 0 ? ` · ${labelledCount} done` : ""}
        </span>
      </div>

      {barsQ.data ? (
        <Chart bars={barsQ.data} overlay={overlay} height={380} />
      ) : (
        <p className="muted">Loading chart…</p>
      )}

      <div className="muted small" style={{ marginTop: 8 }}>
        Spike {String(current.leg_start_date)} → {String(current.leg_end_date)} ·
        Range → {String(current.tr_end_date)} ·
        Score {Number(current.score).toFixed(1)} · Origin {String(current.origin)}
      </div>

      {showNotes && (
        <textarea
          className="notes"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
        />
      )}

      {errMsg && <p className="error">Save failed: {errMsg}</p>}

      <div className="label-footer">
        <button
          onClick={() => labelMut.mutate("bad")}
          disabled={labelMut.isPending}
          className="lbl-bad"
        >
          Bad
        </button>
        <button
          onClick={() => labelMut.mutate("borderline")}
          disabled={labelMut.isPending}
          className="lbl-border"
        >
          Borderline
        </button>
        <button
          onClick={() => labelMut.mutate("good")}
          disabled={labelMut.isPending}
          className="lbl-good"
        >
          Good
        </button>
        <button
          onClick={() => {
            setNotes("");
            setShowNotes(false);
            setCursor((c) => c + 1);
          }}
          disabled={labelMut.isPending}
        >
          Skip ▸
        </button>
        <button
          onClick={() => setShowNotes((v) => !v)}
          disabled={labelMut.isPending}
          aria-pressed={showNotes}
          title="Toggle notes"
        >
          {showNotes ? "Hide notes" : "+ Notes"}
        </button>
      </div>
    </div>
  );
}
